import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import {
  Body,
  Button,
  Colors,
  Input,
  OptionType,
  Select,
  Textarea,
  Tooltip,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { GQLOperations, Ruleset, Milestone } from 'types/dt-graphql-apollo-v2';
import { v1 as uuidv1 } from 'uuid';
import { gql, useApolloClient } from '@apollo/client';
import {
  createRulesetMutation,
  updateRulesetMutation,
} from 'utils/data-validation-graphql';
import {
  getConditionsCount,
  getSchemaTypeOptions,
  term,
} from 'utils/project-ruleset';
import { defaultLogicalRule } from 'utils/nested-conditions';

import { Step, DrawerSteps, LabelListEdit, LabelListDisplay } from 'components';
import { Schema } from 'hooks/projects';
import {
  LogicalRule,
  logicalRuleToFilter,
  parseFilterToLogicalRule,
} from 'utils/rules';
import { DrawerWithTitle } from './components/DrawerWithTitle';
import { ConditionItem } from './components/Conditions/ConditionItem';

const defaultSchemaType = {
  label: `Select ${term.schemaType}`,
  value: '',
};

type NewRulesetDrawerProps = {
  currentSchemas: Schema[];
  onCancel: VoidFunction;
  resetDrawer: boolean;
  schemaId: string;
  selectedMilestone?: Milestone;
  selectedRuleset?: Ruleset;
  visible: boolean;
};

export const NewRulesetDrawer = ({
  currentSchemas,
  onCancel,
  resetDrawer,
  schemaId,
  selectedMilestone,
  selectedRuleset,
  visible,
}: NewRulesetDrawerProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [schemaTypeInput, setSchemaTypeInput] = useState('');
  const [labelListInput, setLabelListInput] = useState<string[]>([]);
  const [filtersTree, setFiltersTree] = useState<LogicalRule>({
    ...defaultLogicalRule,
  });
  const apolloClient = useApolloClient();

  const actualSchemaType = schemaTypeInput || selectedRuleset?.schemaType || '';
  const selectedSchema = currentSchemas?.find(
    (schema) => schema.name === actualSchemaType
  );

  useEffect(() => {
    if (selectedRuleset) {
      setNameInput(selectedRuleset.name || '');
      setDescriptionInput(selectedRuleset.description || '');
      setSchemaTypeInput(selectedRuleset.schemaType || '');
      setLabelListInput((selectedRuleset.labels as string[]) || []);
      const formattedFilters = parseFilterToLogicalRule(
        selectedRuleset.filters || '{}',
        selectedSchema
      );

      setFiltersTree(
        formattedFilters || {
          ...defaultLogicalRule,
        }
      );
    } else {
      onResetDrawer();
    }
  }, [selectedRuleset]);

  const { total: totaFilters, valid: totalValidFilters } =
    getConditionsCount(filtersTree);

  const hasInvalidName = !nameInput.trim();
  const hasInvalidSchemaType = !schemaTypeInput?.length;

  const hasInvalidFilterList = totaFilters !== totalValidFilters;
  const hasInvalidFilters = hasInvalidFilterList || hasInvalidSchemaType;
  const hasInvalidValues = hasInvalidName || hasInvalidFilters;

  const schemaTypeOptions = getSchemaTypeOptions(currentSchemas);
  const selectedSchemaTypeOption =
    schemaTypeOptions.find((st) => st.value === schemaTypeInput) ??
    defaultSchemaType;

  const handleErrorMessages = () => {
    if (hasInvalidName) {
      return 'The provided name is missing or invalid';
    }
    if (hasInvalidSchemaType) {
      return 'The provided schema type is missing or invalid';
    }
    if (hasInvalidFilterList) {
      return 'The provided filters are incomplete or invalid';
    }
    return 'Unable to save the ruleset due to invalid configurations';
  };

  const onResetDrawer = () => {
    setNameInput('');
    setDescriptionInput('');
    setSchemaTypeInput('');
    setLabelListInput([]);
    setFiltersTree({
      ...defaultLogicalRule,
    });
    setCurrentStep(0);
  };

  const onCreateRuleset = async () => {
    if (creating || hasInvalidValues) return;
    setCreating(true);
    const parsedFilters = logicalRuleToFilter(
      filtersTree,
      selectedSchema?.properties || []
    );
    try {
      const milestoneId = selectedMilestone?.externalId;
      const ruleset = {
        externalId: uuidv1(),
        projectId: schemaId,
        name: nameInput.trim(),
        description: descriptionInput.trim(),
        schemaType: schemaTypeInput,
        labels: labelListInput,
        space: '',
      };
      const query = createRulesetMutation(ruleset, milestoneId);
      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          labels: labelListInput,
          filters: JSON.stringify(parsedFilters),
        },
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      notification.success({
        message: `${term.Ruleset} created`,
        description: `Successfully created the ${
          term.ruleset
        } "${nameInput.trim()}"`,
      });
      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not add ${term.ruleset}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onUpdateRuleset = async () => {
    if (creating || hasInvalidValues || !selectedRuleset) return;
    setCreating(true);
    try {
      const parsedFilters = logicalRuleToFilter(
        filtersTree,
        selectedSchema?.properties || []
      );
      const updatedRuleset = {
        externalId: selectedRuleset.externalId,
        projectId: schemaId,
        name: nameInput.trim(),
        description: descriptionInput.trim(),
        schemaType: schemaTypeInput,
        labels: labelListInput,
        space: '',
      };

      const query = updateRulesetMutation(selectedRuleset, updatedRuleset);
      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          labels: labelListInput,
          filters: JSON.stringify(parsedFilters),
        },
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      notification.success({
        message: 'Ruleset updated',
        description: `Successfully updated the ${term.ruleset} "${updatedRuleset.name}"`,
      });
      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not update ${term.ruleset}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    if (selectedRuleset && resetDrawer) {
      onResetDrawer();
    }
    setCurrentStep(0);
    onCancel();
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  const renderNameStep = () => (
    <>
      <Input
        fullWidth
        placeholder="Name"
        value={nameInput}
        onChange={(e) => setNameInput(e.target.value)}
      />
      <StyledTextarea
        placeholder="Description"
        value={descriptionInput}
        onChange={(e) => setDescriptionInput(e.target.value)}
      />

      <Button type="primary" disabled={hasInvalidName} onClick={nextStep}>
        Next
      </Button>
    </>
  );
  const renderNameDescription = () => (
    <StyledStepDescription>
      {nameInput && <StyledBodyBold level={2}>{nameInput}</StyledBodyBold>}
      {descriptionInput && <Body level={2}>{descriptionInput}</Body>}
    </StyledStepDescription>
  );

  const renderLabelsStep = () => (
    <>
      <Body level={2}>The labels can be used as filters in the tool.</Body>
      <LabelListEdit
        disabled={creating}
        labels={labelListInput}
        onChange={(labels: string[]) => setLabelListInput(labels)}
      />
      <Button
        type="primary"
        onClick={() => {
          nextStep();
        }}
      >
        Next
      </Button>
    </>
  );

  const renderLabelsDescription = () => (
    <StyledStepDescription>
      <LabelListDisplay
        labels={labelListInput.map((label) => ({ text: label, key: label }))}
        limitLabels={7}
      />
    </StyledStepDescription>
  );

  const renderTypesStep = () => (
    <>
      <Body level={2}>
        Select the {term.schemaType} to check and add filters to further specify
        the objects to be checked. The objects that match the filter are
        included in the check.
      </Body>
      <SelectWrapperSchemaType>
        <Select
          fullWidth
          closeMenuOnSelect
          value={selectedSchemaTypeOption}
          placeholder={`Select ${term.schemaType}`}
          onChange={(option: OptionType<string>) =>
            setSchemaTypeInput(option.value ?? '')
          }
          options={schemaTypeOptions}
        />
      </SelectWrapperSchemaType>
    </>
  );

  const renderFiltersStep = () => (
    <>
      <StyledText level={2}>
        Define filters to apply checks to a subset of the {term.schemaType}.
      </StyledText>
      <Group>
        <ConditionItem
          schemas={currentSchemas}
          ancestors={[]}
          selectedSchema={selectedSchema}
          root={filtersTree}
          condition={filtersTree}
          onRootUpdate={setFiltersTree}
          isFilter
          enabledPropertyComparison={false}
        />
      </Group>
      <StyledButtonNext
        type="primary"
        disabled={hasInvalidFilters}
        onClick={nextStep}
      >
        Next
      </StyledButtonNext>
    </>
  );

  const renderFilterDescription = () => (
    <StyledStepDescription>
      {schemaTypeInput && (
        <StyledBodyBold level={2}>
          {term.SchemaType}: {selectedSchemaTypeOption.label}
        </StyledBodyBold>
      )}
      <StyledBodyBold level={2}>{totalValidFilters} conditions</StyledBodyBold>
    </StyledStepDescription>
  );

  const stepSubtitle = (
    <Button type="ghost" icon="Edit" size="small" aria-label="Edit" />
  );
  const fallBackDescription = 'No details';
  const steps: Step[] = [
    {
      key: 'description',
      title: 'General information',
      subTitle: stepSubtitle,
      description:
        nameInput || descriptionInput
          ? renderNameDescription()
          : fallBackDescription,
      content: renderNameStep(),
      invalid: hasInvalidName,
    },
    {
      key: 'labels',
      title: 'Labels',
      subTitle: stepSubtitle,
      description: labelListInput.length
        ? renderLabelsDescription()
        : fallBackDescription,
      content: renderLabelsStep(),
      invalid: false,
    },
    {
      key: 'filter',
      title: `${term.Ruleset} filters`,
      subTitle: stepSubtitle,
      description: schemaTypeInput
        ? renderFilterDescription()
        : fallBackDescription,
      content: (
        <>
          {renderTypesStep()}
          {renderFiltersStep()}
        </>
      ),
      invalid: hasInvalidFilters,
    },
  ];

  const renderFooter = () => (
    <StyledFooter>
      <Button onClick={onCancelHandler}>Cancel</Button>
      <Tooltip
        content={handleErrorMessages()}
        disabled={!creating && !hasInvalidValues}
      >
        <Button
          type="primary"
          onClick={() =>
            selectedRuleset ? onUpdateRuleset() : onCreateRuleset()
          }
          loading={creating}
          disabled={creating || hasInvalidValues}
        >
          {selectedRuleset
            ? `Update ${term.ruleset}`
            : `Create ${term.ruleset}`}
        </Button>
      </Tooltip>
    </StyledFooter>
  );

  return (
    <DrawerWithTitle
      footer={renderFooter()}
      onCancel={onCancelHandler}
      selectedMilestoneName={selectedMilestone?.name}
      subHeader={
        <Body level={2}>
          Set filters to define a group of instances on which to apply related
          rules
        </Body>
      }
      title={`${selectedRuleset ? 'Edit' : 'Create'} ${term.ruleset} `}
      visible={visible}
    >
      <DrawerSteps
        steps={steps}
        currentStep={currentStep}
        onChangeStep={(step: number) => setCurrentStep(step)}
      />
    </DrawerWithTitle>
  );
};

const StyledBodyBold = styled(Body)`
  font-weight: 500;
`;

const StyledStepDescription = styled.div`
  margin-top: 0.5rem;
`;

const StyledText = styled(Body)`
  margin-top: 1.5rem;
`;

const Group = styled.div``;

const StyledTextarea = styled(Textarea)`
  min-height: 100px;
  flex-direction: column;
  display: flex;
`;

const SelectWrapper = styled.div`
  max-width: 500px;
  width: 100%;
  .cogs-select {
    .cogs-select__menu {
      max-width: 250px;
      max-height: 500px;
      overflow: auto;
    }

    .cogs-select__value-container > div {
      width: 100% !important;
    }
    .cogs-select__value-container .cogs-select__input {
      &,
      input {
        width: 100% !important;
        display: inline-block;
      }
    }
    .cogs-select__control {
      max-width: 500px;
      background: ${Colors['decorative--grayscale--white']};
    }
    .cogs-select__control .cogs-select__single-value,
    .cogs-select__menu .cogs-select__option {
      font-style: normal;
      font-weight: 400;
      font-size: var(--cogs-font-size-sm);
      line-height: 20px;
      color: ${Colors['decorative--grayscale--1000']};
      &:hover {
        cursor: pointer;
      }
    }
  }
  &:hover {
    cursor: pointer;
  }
`;

const SelectWrapperSchemaType = styled(SelectWrapper)`
  .cogs-select {
    .cogs-select--container {
      max-width: 180px;
    }
  }
`;

const StyledButtonNext = styled(Button)`
  margin-top: 2.5rem;
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
`;
